import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, AlertTriangle, ShieldCheck, Repeat, RefreshCw } from "lucide-react";
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";

interface EventCounts {
  verifier_pass: number;
  verifier_fail: number;
  corrective_regen_pass: number;
  corrective_regen_fail: number;
  repetition_regenerated: number;
  repetition_loop_blocked: number;
  self_obsession_shortcircuit: number;
}

const EMPTY: EventCounts = {
  verifier_pass: 0, verifier_fail: 0,
  corrective_regen_pass: 0, corrective_regen_fail: 0,
  repetition_regenerated: 0, repetition_loop_blocked: 0,
  self_obsession_shortcircuit: 0,
};

export default function HallucinationHealth() {
  const [counts, setCounts] = useState<EventCounts>(EMPTY);
  const [recent, setRecent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from("hallucination_events")
      .select("event_type, created_at, details, group_id")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(500);
    const next = { ...EMPTY };
    for (const r of data ?? []) {
      const k = r.event_type as keyof EventCounts;
      if (k in next) next[k] += 1;
    }
    setCounts(next);
    setRecent((data ?? []).slice(0, 20));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const total = counts.verifier_pass + counts.verifier_fail;
  const failRate = total > 0 ? (counts.verifier_fail / total) * 100 : 0;

  return (
    <div className="container mx-auto py-8 space-y-6">
      <ShadowModeBanner context="Detector e shield anti-alucinação foram desligados (rollout 0%) — números refletem apenas o legacy." compact />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Saúde Anti-Alucinação</h1>
          <p className="text-sm text-muted-foreground">Janela: últimas 24h</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Recarregar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Verifier pass</CardTitle>
          </CardHeader>
          <CardContent><p className="text-3xl font-semibold">{counts.verifier_pass}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <CardTitle className="text-sm">Verifier fail</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{counts.verifier_fail}</p>
            <p className="text-xs text-muted-foreground mt-1">Taxa: {failRate.toFixed(1)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Corrective regen ✓</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{counts.corrective_regen_pass}</p>
            <p className="text-xs text-muted-foreground mt-1">Recuperações: {counts.corrective_regen_pass}/{counts.corrective_regen_pass + counts.corrective_regen_fail}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center gap-2">
            <Repeat className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Repetition regen</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{counts.repetition_regenerated}</p>
            <p className="text-xs text-muted-foreground mt-1">Loops bloqueados: {counts.repetition_loop_blocked}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Self-obsession shortcircuits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-semibold">{counts.self_obsession_shortcircuit}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Turnos abortados por loop temático nas últimas 24h. Flag <code>cortex.antihal.self_obsession_shortcircuit</code>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Eventos recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem eventos em 24h.</p>
          ) : (
            <div className="space-y-2">
              {recent.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-border last:border-0 pb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={r.event_type.includes("fail") || r.event_type.includes("blocked") || r.event_type.includes("shortcircuit") ? "destructive" : "outline"}>
                      {r.event_type}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  {r.details?.confidence != null && (
                    <span className="text-xs text-muted-foreground">conf: {Number(r.details.confidence).toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inspecionar turno específico</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-2">
            Use a página <Link to="/cortex-os" className="underline">Cortex OS</Link> para listar turnos recentes,
            ou acesse <code>/cortex-os/turn/&lt;turn_id&gt;</code> diretamente.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
